import type {
  ChatItem,
  ChatMessage,
  ClassHomeItem,
  ClassItem,
  FileItem,
  JobItem,
  MemberItem,
  RecentChatItem,
  User,
} from "./types";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...init,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error || `Request failed with status ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export const api = {
  bootstrapStatus: () =>
    request<{
      hasUsers: boolean;
      allowAdminBootstrap: boolean;
      bootstrapMode: "open" | "restricted" | "closed";
    }>("/api/bootstrap/status"),
  me: () => request<{ user: User | null }>("/api/me"),
  home: () => request<{ classes: ClassHomeItem[]; recentChats: RecentChatItem[] }>("/api/home"),
  register: (payload: {
    email: string;
    displayName: string;
    password: string;
    role: User["role"];
  }) =>
    request<{ user: User; bootstrap: { wasFirstUser: boolean } }>("/api/auth/register", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }),
  login: (payload: { email: string; password: string }) =>
    request<{ user: User }>("/api/auth/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }),
  logout: () =>
    request<{ ok: true }>("/api/auth/logout", {
      method: "POST",
    }),
  listClasses: () => request<{ classes: ClassItem[] }>("/api/classes"),
  createClass: (payload: { name: string; subject: string; gradeBand: string }) =>
    request<{ class: ClassItem }>("/api/classes", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }),
  listMembers: (classId: string) =>
    request<{ members: MemberItem[] }>(`/api/classes/${classId}/members`),
  addMember: (classId: string, payload: { email: string; role: "TEACHER" | "STUDENT" }) =>
    request<{ ok: true }>(`/api/classes/${classId}/members`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }),
  listFiles: (classId: string) =>
    request<{ files: FileItem[] }>(`/api/classes/${classId}/files`),
  uploadFile: async (classId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<{ ok: true; fileId: string; jobId: string }>(
      `/api/classes/${classId}/files`,
      {
        method: "POST",
        body: form,
      },
    );
  },
  listChats: (classId: string | null) =>
    request<{ chats: ChatItem[] }>(
      classId ? `/api/chats?classId=${encodeURIComponent(classId)}` : "/api/chats",
    ),
  createChat: (payload: { classId: string | null; title: string }) =>
    request<{ chat: ChatItem }>("/api/chats", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }),
  listMessages: (chatId: string) =>
    request<{ messages: ChatMessage[] }>(`/api/chats/${chatId}/messages`),
  sendMessage: (chatId: string, payload: { content: string; mode?: "chat" | "image" }) =>
    request<{ message: ChatMessage; jobId: string }>(`/api/chats/${chatId}/messages`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }),
  listJobs: (classId: string | null) =>
    request<{ jobs: JobItem[] }>(
      classId ? `/api/jobs?classId=${encodeURIComponent(classId)}` : "/api/jobs",
    ),
};
